import { ReactNode, useCallback, useMemo, useState, useEffect } from "react";
import styles from "@/pages/index.module.css";
import { Step } from "@/typings/step";

const TimerMarks = new Array(12).fill(0).map((value, index) => index);

type TimerProps = {
  step: Step;
  onUpdateStep: (step: Step) => void;
};

function getTimerInnerStyle(step: Step) {
  switch (step) {
    case Step.RoundOne:
    case Step.RoundOneGuessing:
    case Step.RoundOneFail:
      return styles.timerInnerRoundOne;
    case Step.RoundTwo:
    case Step.RoundTwoGuessing:
    case Step.RoundTwoFail:
      return styles.timerInnerRoundTwo;
    case Step.RoundThree:
    case Step.RoundThreeGuessing:
    case Step.RoundThreeFail:
      return styles.timerInnerRoundThree;
    default:
      return "";
  }
}

function getTimerHandStyle(step: Step) {
  switch (step) {
    case Step.RoundOne:
      return styles.timerHandRoundOneInProgress;
    case Step.RoundOneGuessing:
    case Step.RoundOneFail:
      return styles.timerHandRoundOneEnded;
    case Step.RoundTwo:
      return styles.timerHandRoundTwoInProgress;
    case Step.RoundTwoGuessing:
    case Step.RoundTwoFail:
      return styles.timerHandRoundTwoEnded;
    case Step.RoundThree:
      return styles.timerHandRoundThreeInProgress;
    case Step.RoundThreeGuessing:
      return styles.timerHandRoundThreeEnded;
    default:
      return "";
  }
}

function getNextStep(step: Step) {
  switch (step) {
    case Step.RoundOne:
      return Step.RoundOneGuessing;
    case Step.RoundTwo:
      return Step.RoundTwoGuessing;
    case Step.RoundThree:
      return Step.RoundThreeGuessing;
    default:
      return null;
  }
}

export function Timer({ step, onUpdateStep }: TimerProps): ReactNode {
  const nextStepIfExists = useMemo(() => getNextStep(step), [step]);

  const onEndRound = useCallback(() => {
    if (!nextStepIfExists) {
      return;
    }

    onUpdateStep(nextStepIfExists);
  }, [nextStepIfExists, onUpdateStep]);

  const [timerHandStyle, setTimerHandStyle] = useState("");
  useEffect(() => {
    const newStyle = getTimerHandStyle(step);
    if (newStyle.length) {
      setTimerHandStyle(newStyle);
    }
  }, [step]);

  const [timerInnerStyle, setTimerInnerStyle] = useState("");
  useEffect(() => {
    const newStyle = getTimerInnerStyle(step);
    if (newStyle.length) {
      setTimerInnerStyle(newStyle);
    }
  }, [step]);

  return (
    <div className={styles.timerOuter}>
      <div className={styles.timer}>
        <div className={`${styles.timerInner} ${timerInnerStyle}`}>
          {TimerMarks.map((value) => (
            <div
              key={value}
              className={styles.timerMark}
              style={{ transform: `rotate(${value * 30}deg)` }}
            />
          ))}
          <div className={`${styles.timerHand} ${timerHandStyle}`} />
        </div>
      </div>
      <button
        aria-label="End round"
        className={`${styles.timerButton} ${nextStepIfExists ? "" : styles.timerButtonStopped}`}
        onClick={onEndRound}
      >
        {nextStepIfExists && <div className={styles.timerButtonText}>Done!</div>}
      </button>
    </div>
  );
}
